import { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer } from "react-leaflet";
import { MapContent } from "../MapContent";
import { HF_BASE, MIN_YEAR, MAX_YEAR, yearLabel, resolveFullName } from "../mapUtils";
import {
  dbFetchProfileGames,
  dbFetchProfileHistory,
  dbFetchProfileRounds,
  dbFetchProfileSummary,
  dbFetchRoundsByGameIds,
  dbFetchUserProfile,
  dbUpsertUserProfile,
} from "../db";

const ERA_KEYS = Array.from({ length: 41 }, (_, index) => -2000 + index * 100);

const eraLabel = (year) => year < 0 ? `${Math.abs(year)}s BCE` : `${year}s`;

const initialOptions = {
  performanceView: "era",
  historyOrder: "recent",
  pageSize: 5,
};

const defaultProfile = {
  about: "",
  favoritePeriodStart: MIN_YEAR,
  favoritePeriodEnd: MAX_YEAR,
  favoriteRulerKey: "",
  favoriteRulerYear: 1000,
  favoriteRegionName: "",
  favoriteRegionYear: 1000,
};

function getStoredOptions() {
  try {
    const raw = window.localStorage.getItem("mapguessr-profile-options");
    return raw ? JSON.parse(raw) : initialOptions;
  } catch {
    return initialOptions;
  }
}

function saveOptions(opts) {
  try {
    window.localStorage.setItem("mapguessr-profile-options", JSON.stringify(opts));
  } catch {
    // ignore local storage failures
  }
}

function buildEraStats(rounds) {
  const bins = ERA_KEYS.reduce((acc, year) => {
    acc[year] = { label: eraLabel(year), total: 0, count: 0 };
    return acc;
  }, {});

  rounds.forEach((round) => {
    let key = Math.floor(round.actual_year / 100) * 100;
    if (key < -2000) key = -2000;
    if (key > 2000) key = 2000;
    if (!bins[key]) return;
    bins[key].total += round.score;
    bins[key].count += 1;
  });

  return ERA_KEYS.map((year) => ({
    key: year,
    label: bins[year].label,
    scoreAvg: bins[year].count ? bins[year].total / bins[year].count : 0,
    games: bins[year].count,
  }));
}

function buildFavoriteMap(provinces, yearData, metadata, type, highlightValue) {
  if (!provinces || !yearData || !metadata) return null;
  return {
    ...provinces,
    features: provinces.features.map((feature) => {
      const info = yearData[feature.properties.name];
      if (!info) return feature;
      const [rulerKey, cultureKey, religionKey, capital] = info;
      const rulerName = resolveFullName(metadata, "ruler", rulerKey);
      const cultureName = resolveFullName(metadata, "culture", cultureKey);
      const religionName = resolveFullName(metadata, "religion", religionKey);
      const isHighlight = type === "ruler"
        ? rulerKey === highlightValue
        : type === "province"
          ? feature.properties.name === highlightValue
          : false;
      return {
        ...feature,
        properties: {
          ...feature.properties,
          rulerKey,
          cultureKey,
          religionKey,
          capital,
          rulerName,
          cultureName,
          religionName,
          highlight: isHighlight,
          color: isHighlight ? "#6fcf97" : "#12202d",
        },
      };
    }),
  };
}

function clampYear(value) {
  if (Number.isNaN(value)) return MIN_YEAR;
  return Math.max(MIN_YEAR, Math.min(MAX_YEAR, Math.round(value)));
}

function formatPeriodLabel(start, end) {
  return `${yearLabel(start)} – ${yearLabel(end)}`;
}

export default function ProfileScreen({ target, provinces, metadata, onBack, isCurrentUser }) {
  const [summary, setSummary] = useState(null);
  const [eraStats, setEraStats] = useState([]);
  const [allGames, setAllGames] = useState([]);
  const [profileData, setProfileData] = useState(defaultProfile);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [historyCount, setHistoryCount] = useState(0);
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState(initialOptions);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedGames, setExpandedGames] = useState({});
  const [saving, setSaving] = useState(false);
  const [rulerSelectYear, setRulerSelectYear] = useState(1000);
  const [provinceSelectYear, setProvinceSelectYear] = useState(1000);
  const [rulerYearData, setRulerYearData] = useState(null);
  const [provinceYearData, setProvinceYearData] = useState(null);

  useEffect(() => {
    setOptions(getStoredOptions());
  }, []);

  useEffect(() => {
    if (!target?.userId) return;
    setLoading(true);
    (async () => {
      try {
        const [summaryData, rounds, allGamesData, userProfile] = await Promise.all([
          dbFetchProfileSummary(target.userId),
          dbFetchProfileRounds(target.userId),
          dbFetchProfileGames(target.userId, 100),
          dbFetchUserProfile(target.userId),
        ]);

        const storedProfile = {
          ...defaultProfile,
          ...userProfile,
        };
        
        setProfileData(storedProfile);
        setRulerSelectYear(storedProfile.favoriteRulerYear || 1000);
        setProvinceSelectYear(storedProfile.favoriteRegionYear || 1000);

        let eraRounds = rounds || [];
        if ((!eraRounds.length || eraRounds.every((r) => r.score == null)) && allGamesData.length > 0) {
          const fallbackRounds = await dbFetchRoundsByGameIds(allGamesData.map((game) => game.id));
          if (fallbackRounds.length) {
            eraRounds = fallbackRounds;
          }
        }

        setSummary(summaryData);
        setEraStats(buildEraStats(eraRounds));
        setAllGames(allGamesData || []);
      } catch (err) {
        console.error("ProfileScreen load error:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [target]);

  useEffect(() => {
    if (!target?.userId) return;
    setHistoryLoading(true);
    dbFetchProfileHistory(target.userId, page, options.pageSize)
      .then(({ data, count }) => {
        setHistory(data || []);
        setHistoryCount(count || 0);
      })
      .catch((err) => console.error("ProfileScreen history error:", err))
      .finally(() => setHistoryLoading(false));
  }, [target, page, options.pageSize]);

  useEffect(() => {
    if (!target?.userId) return;
    const missingHistory = history.filter((g) => !g.rounds?.length && g.rounds_played > 0).map((g) => g.id);
    if (missingHistory.length === 0) return;

    dbFetchRoundsByGameIds(missingHistory)
      .then((roundRows) => {
        if (!roundRows.length) return;
        const roundsByGame = roundRows.reduce((acc, round) => {
          acc[round.game_id] = acc[round.game_id] || [];
          acc[round.game_id].push(round);
          return acc;
        }, {});
        setHistory((current) => current.map((g) => ({
          ...g,
          rounds: roundsByGame[g.id] ? roundsByGame[g.id].sort((a, b) => a.round_number - b.round_number) : g.rounds,
        })));
      })
      .catch((err) => console.error("ProfileScreen fallback history rounds error:", err));
  }, [history, target?.userId]);

  useEffect(() => {
    saveOptions(options);
  }, [options]);

  useEffect(() => {
    setPage(1);
  }, [options.pageSize, target?.userId]);

  useEffect(() => {
    let active = true;
    const snap = Math.round(rulerSelectYear / 10) * 10;
    fetch(`${HF_BASE}/attrs/${snap}.json`)
      .then((res) => res.ok ? res.json() : null)
      .then((data) => { if (active) setRulerYearData(data); })
      .catch(() => { if (active) setRulerYearData(null); });
    return () => { active = false; };
  }, [rulerSelectYear]);

  useEffect(() => {
    let active = true;
    const snap = Math.round(provinceSelectYear / 10) * 10;
    fetch(`${HF_BASE}/attrs/${snap}.json`)
      .then((res) => res.ok ? res.json() : null)
      .then((data) => { if (active) setProvinceYearData(data); })
      .catch(() => { if (active) setProvinceYearData(null); });
    return () => { active = false; };
  }, [provinceSelectYear]);

  const name = target?.displayName || summary?.display_name || "Anonymous";
  const gamesPlayed = summary?.games_played ?? (summary ? 0 : null);
  const best = summary?.best_score ?? null;
  const avg = summary?.avg_score ?? null;

  const sortedHistory = useMemo(() => {
    if (!history) return [];
    const list = [...history];
    if (options.historyOrder === "highest") {
      list.sort((a, b) => b.total_score - a.total_score);
    } else {
      list.sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));
    }
    return list;
  }, [history, options.historyOrder]);

  const timelineSeries = useMemo(() => {
    const games = (allGames || [])
      .map((game, index) => {
        const completedAt = game.completed_at ? new Date(game.completed_at) : null;
        return {
          key: game.id,
          score: game.total_score,
          date: completedAt,
          label: completedAt ? completedAt.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : `Game ${index + 1}`,
        };
      })
      .filter((item) => item.date);
    if (!games.length) return [];
    const minDate = Math.min(...games.map((item) => item.date.getTime()));
    const maxDate = Math.max(...games.map((item) => item.date.getTime()));
    const minScore = Math.min(...games.map((item) => item.score), 0);
    const maxScore = Math.max(...games.map((item) => item.score), 1);
    const chartWidth = Math.max(games.length * 80, 640);
    const chartHeight = 220;
    return games.map((item) => ({
      ...item,
      x: 40 + ((item.date.getTime() - minDate) / Math.max(maxDate - minDate, 1)) * (chartWidth - 80),
      y: 20 + ((maxScore - item.score) / Math.max(maxScore - minScore, 1)) * (chartHeight - 40),
      chartWidth,
      chartHeight,
      minScore,
      maxScore,
      minDate,
      maxDate,
    }));
  }, [allGames]);

  const pageCount = Math.max(1, Math.ceil(historyCount / options.pageSize));

  const rulerOptions = useMemo(() => {
    if (!rulerYearData || !metadata) return [];
    const map = {};
    Object.entries(rulerYearData).forEach(([name, info]) => {
      const rulerKey = info?.[0];
      if (!rulerKey) return;
      map[rulerKey] = resolveFullName(metadata, "ruler", rulerKey);
    });
    return Object.entries(map).map(([key, label]) => ({ key, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [rulerYearData, metadata]);

  const provinceOptions = useMemo(() => {
    if (!provinceYearData) return [];
    return Object.keys(provinceYearData).sort((a, b) => a.localeCompare(b));
  }, [provinceYearData]);

  const rulerMap = useMemo(() => buildFavoriteMap(provinces, rulerYearData, metadata, "ruler", profileData.favoriteRulerKey), [provinces, rulerYearData, metadata, profileData.favoriteRulerKey]);
  const provinceMap = useMemo(() => buildFavoriteMap(provinces, provinceYearData, metadata, "province", profileData.favoriteRegionName), [provinces, provinceYearData, metadata, profileData.favoriteRegionName]);

  const selectedRulerLabel = profileData.favoriteRulerKey && metadata ? resolveFullName(metadata, "ruler", profileData.favoriteRulerKey) : "Not selected";
  const selectedProvinceLabel = profileData.favoriteRegionName || "Not selected";

  const profileYearStyle = useMemo(() => {
    const startPct = ((profileData.favoritePeriodStart - MIN_YEAR) / (MAX_YEAR - MIN_YEAR)) * 100;
    const endPct = ((profileData.favoritePeriodEnd - MIN_YEAR) / (MAX_YEAR - MIN_YEAR)) * 100;
    return { left: `${startPct}%`, width: `${Math.max(2, endPct - startPct)}%` };
  }, [profileData.favoritePeriodStart, profileData.favoritePeriodEnd]);

  const saveProfile = useCallback(async () => {
    if (!isCurrentUser || !target?.userId) return;
    setSaving(true);
    try {
      const saved = await dbUpsertUserProfile({ user_id: target.userId, ...profileData });
      if (saved) {
        setProfileData((prev) => ({ ...prev, ...saved }));
      }
    } catch (err) {
      console.error("ProfileScreen save error:", err);
    } finally {
      setSaving(false);
    }
  }, [isCurrentUser, profileData, target?.userId]);

  const saveButtonLabel = saving ? "Saving..." : "Save profile";

  return (
    <div style={{ background: "#0a0f16", minHeight: "100vh", fontFamily: "Georgia, serif", color: "#e8dcc8" }}>
      <div style={{ padding: "14px 20px", borderBottom: "1px solid #1a2530", display: "flex", alignItems: "center", gap: 16 }}>
        <span onClick={onBack} style={{ color: "#c9a84c", fontSize: 11, letterSpacing: 2, cursor: "pointer", opacity: 0.7 }}>← BACK</span>
        <div>
          <div style={{ color: "#c9a84c", fontSize: 13, letterSpacing: 4 }}>{isCurrentUser ? "YOUR PROFILE" : "PLAYER PROFILE"}</div>
          <div style={{ fontSize: 11, color: "#9aa6b8", marginTop: 3 }}>{name}</div>
        </div>
      </div>

      <div style={{ maxWidth: 980, margin: "0 auto", padding: "24px 16px", display: "flex", flexDirection: "column", gap: 18 }}>
        {loading && (
          <div style={{ textAlign: "center", color: "#4a5a6a", letterSpacing: 3, fontSize: 11, marginTop: 40 }}>LOADING PROFILE...</div>
        )}

        {!loading && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div style={{ background: "#0d1520", border: "1px solid #1e2e3e", borderRadius: 12, padding: 18 }}>
                <div style={{ fontSize: 10, color: "#4a5a6a", letterSpacing: 2, textTransform: "uppercase" }}>Games</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: "#e8dcc8", marginTop: 10 }}>{gamesPlayed ?? "—"}</div>
              </div>
              <div style={{ background: "#0d1520", border: "1px solid #1e2e3e", borderRadius: 12, padding: 18 }}>
                <div style={{ fontSize: 10, color: "#4a5a6a", letterSpacing: 2, textTransform: "uppercase" }}>Best score</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: "#6fcf97", marginTop: 10 }}>{best ?? "—"}</div>
              </div>
              <div style={{ background: "#0d1520", border: "1px solid #1e2e3e", borderRadius: 12, padding: 18 }}>
                <div style={{ fontSize: 10, color: "#4a5a6a", letterSpacing: 2, textTransform: "uppercase" }}>Average score</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: "#c9a84c", marginTop: 10 }}>{avg ? Math.round(avg) : "—"}</div>
              </div>
            </div>

            <div style={{ background: "#0d1520", border: "1px solid #1e2e3e", borderRadius: 12, padding: 18 }}>
              <div style={{ fontSize: 11, color: "#c9a84c", letterSpacing: 2, textTransform: "uppercase", marginBottom: 14 }}>Profile customization</div>
              {isCurrentUser ? (
                <div style={{ display: "grid", gap: 16 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <label style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 11, color: "#9aa6b8" }}>
                      About me
                      <textarea rows={4} value={profileData.about}
                        onChange={(e) => setProfileData((prev) => ({ ...prev, about: e.target.value }))}
                        style={{ resize: "vertical", minHeight: 90, background: "#0b1320", border: "1px solid #1e2e3e", color: "#e8dcc8", borderRadius: 10, padding: 10 }} />
                    </label>
                    <div style={{ display: "grid", gap: 12 }}>
                      <div style={{ display: "grid", gap: 8, fontSize: 11, color: "#9aa6b8" }}>

  <span>Favorite time period</span>
  
  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
    <label style={{ minWidth: 30, fontSize: 11, color: "#9aa6b8" }}>From</label>
    <input type="range" min={MIN_YEAR} max={MAX_YEAR} step={50} 
      value={profileData.favoritePeriodStart}
      onChange={(e) => {
        const value = clampYear(Number(e.target.value));
        setProfileData((prev) => ({
          ...prev,
          favoritePeriodStart: value,
          favoritePeriodEnd: Math.max(value, prev.favoritePeriodEnd),
        }));
      }}
      style={{ flex: 1, accentColor: "#6fcf97" }} />
    <span style={{ fontSize: 11, color: "#e8dcc8", minWidth: 90 }}>{yearLabel(profileData.favoritePeriodStart)}</span>
  </div>

  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
    <label style={{ minWidth: 30, fontSize: 11, color: "#9aa6b8" }}>To</label>
    <input type="range" min={MIN_YEAR} max={MAX_YEAR} step={50}
      value={profileData.favoritePeriodEnd}
      onChange={(e) => {
        const value = clampYear(Number(e.target.value));
        setProfileData((prev) => ({
          ...prev,
          favoritePeriodStart: Math.min(prev.favoritePeriodStart, value),
          favoritePeriodEnd: value,
        }));
      }}
      style={{ flex: 1, accentColor: "#6fcf97" }} />
    <span style={{ fontSize: 11, color: "#e8dcc8", minWidth: 90 }}>{yearLabel(profileData.favoritePeriodEnd)}</span>
  </div>

  <div style={{ position: "relative", height: 12, background: "#08101d", borderRadius: 10, overflow: "hidden" }}>
    <div style={{ position: "absolute", top: 0, bottom: 0, ...profileYearStyle, background: "#6fcf97", borderRadius: 10 }} />
  </div>
  <div style={{ fontSize: 11, color: "#9aa6b8" }}>{formatPeriodLabel(profileData.favoritePeriodStart, profileData.favoritePeriodEnd)}</div>
</div>
                        

                      <button onClick={saveProfile} disabled={saving}
                        style={{ background: saving ? "#131b27" : "#c9a84c", border: "none", borderRadius: 10, color: "#0d1117", padding: "12px 14px", fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", fontSize: 12 }}>
                        {saveButtonLabel}
                      </button>
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div style={{ background: "#08101d", border: "1px solid #1e2e3e", borderRadius: 12, padding: 14 }}>
                      <div style={{ fontSize: 11, color: "#c9a84c", textTransform: "uppercase", letterSpacing: 2, marginBottom: 10 }}>Favorite ruler</div>
                      <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
                        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                          <label style={{ fontSize: 11, color: "#9aa6b8" }}>Era</label>
                          <input type="range" min={MIN_YEAR} max={MAX_YEAR} step={1} value={rulerSelectYear}
                            onChange={(e) => setRulerSelectYear(Number(e.target.value))}
                            style={{ flex: 1, accentColor: "#c9a84c" }} />
                          <span style={{ fontSize: 11, color: "#e8dcc8", minWidth: 90 }}>{yearLabel(rulerSelectYear)}</span>
                        </div>
                        <select value={profileData.favoriteRulerKey}
                          onChange={(e) => setProfileData((prev) => ({ ...prev, favoriteRulerKey: e.target.value, favoriteRulerYear: rulerSelectYear }))}
                          style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #1e2e3e", background: "#0b1320", color: "#e8dcc8" }}>
                          <option value="">Select a ruler</option>
                          {rulerOptions.map((option) => (
                            <option key={option.key} value={option.key}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                      <div style={{ height: 240, marginTop: 12 }}>
                        {rulerYearData ? (
                          <MapContainer center={[20, 0]} zoom={2} minZoom={2} style={{ height: "100%", width: "100%" }}>
                            <MapContent
                              mergedGeoJSON={buildFavoriteMap(provinces, rulerYearData, metadata, "ruler", profileData.favoriteRulerKey)}
                              groupedLabels={[]}
                              showLabels={false}
                              showMarkers={false}
                              markerData={null}
                              showBorders={false}
                              colorMode="ruler"
                              geoKey={`ruler-${rulerSelectYear}-${profileData.favoriteRulerKey}`}
                              onEachFeature={() => {}}
                              styleFeature={(feature) => ({
                                fillColor: feature.properties.color || "#12202d",
                                weight: 0,
                                color: "transparent",
                                fillOpacity: feature.properties.highlight ? 0.92 : 0.14,
                              })}
                              paneOpacity={1}
                            />
                          </MapContainer>
                        ) : (
                          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#9aa6b8", fontSize: 12 }}>Loading ruler preview…</div>
                        )}
                      </div>
                    </div>

                    <div style={{ background: "#08101d", border: "1px solid #1e2e3e", borderRadius: 12, padding: 14 }}>
                      <div style={{ fontSize: 11, color: "#c9a84c", textTransform: "uppercase", letterSpacing: 2, marginBottom: 10 }}>Favorite province</div>
                      <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
                        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                          <label style={{ fontSize: 11, color: "#9aa6b8" }}>Era</label>
                          <input type="range" min={MIN_YEAR} max={MAX_YEAR} step={1} value={provinceSelectYear}
                            onChange={(e) => setProvinceSelectYear(Number(e.target.value))}
                            style={{ flex: 1, accentColor: "#c9a84c" }} />
                          <span style={{ fontSize: 11, color: "#e8dcc8", minWidth: 90 }}>{yearLabel(provinceSelectYear)}</span>
                        </div>
                        <select value={profileData.favoriteRegionName}
                          onChange={(e) => setProfileData((prev) => ({ ...prev, favoriteRegionName: e.target.value, favoriteRegionYear: provinceSelectYear }))}
                          style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #1e2e3e", background: "#0b1320", color: "#e8dcc8" }}>
                          <option value="">Select a province</option>
                          {provinceOptions.map((name) => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      </div>
                      <div style={{ height: 240, marginTop: 12 }}>
                        {provinceYearData ? (
                          <MapContainer center={[20, 0]} zoom={2} minZoom={2} style={{ height: "100%", width: "100%" }}>
                            <MapContent
                              mergedGeoJSON={buildFavoriteMap(provinces, provinceYearData, metadata, "province", profileData.favoriteRegionName)}
                              groupedLabels={[]}
                              showLabels={false}
                              showMarkers={false}
                              markerData={null}
                              showBorders={false}
                              colorMode="ruler"
                              geoKey={`province-${provinceSelectYear}-${profileData.favoriteRegionName}`}
                              onEachFeature={() => {}}
                              styleFeature={(feature) => ({
                                fillColor: feature.properties.color || "#12202d",
                                weight: 0,
                                color: "transparent",
                                fillOpacity: feature.properties.highlight ? 0.92 : 0.14,
                              })}
                              paneOpacity={1}
                            />
                          </MapContainer>
                        ) : (
                          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#9aa6b8", fontSize: 12 }}>Loading province preview…</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ display: "grid", gap: 18 }}>
                  <div style={{ fontSize: 12, color: "#9aa6b8", lineHeight: 1.6 }}>About me:</div>
                  <div style={{ background: "#08101d", border: "1px solid #1e2e3e", borderRadius: 12, padding: 16, minHeight: 80, color: "#e8dcc8" }}>
                    {profileData.about || "This player has not added a profile description yet."}
                  </div>
                  <div style={{ display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 11, color: "#c9a84c", textTransform: "uppercase", letterSpacing: 2 }}>Favorite time period</div>
                    <div style={{ background: "#08101d", border: "1px solid #1e2e3e", borderRadius: 12, padding: 16 }}>
                      <div style={{ fontSize: 12, color: "#e8dcc8", marginBottom: 8 }}>{formatPeriodLabel(profileData.favoritePeriodStart, profileData.favoritePeriodEnd)}</div>
                      <div style={{ position: "relative", height: 12, background: "#0a111d", borderRadius: 10, overflow: "hidden" }}>
                        <div style={{ position: "absolute", top: 0, bottom: 0, ...profileYearStyle, background: "#6fcf97", borderRadius: 10 }} />
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 11, color: "#c9a84c", textTransform: "uppercase", letterSpacing: 2 }}>Favorite ruler</div>
                    <div style={{ background: "#08101d", border: "1px solid #1e2e3e", borderRadius: 12, padding: 16 }}>
                      <div style={{ fontSize: 12, color: "#e8dcc8", marginBottom: 8 }}>{selectedRulerLabel}</div>
                      {profileData.favoriteRulerKey ? (
                        <div style={{ height: 220 }}>
                          <MapContainer center={[20, 0]} zoom={2} minZoom={2} style={{ height: "100%", width: "100%" }}>
                            <MapContent
                              mergedGeoJSON={rulerMap}
                              groupedLabels={[]}
                              showLabels={false}
                              showMarkers={false}
                              markerData={null}
                              showBorders={false}
                              colorMode="ruler"
                              geoKey={`view-ruler-${profileData.favoriteRulerYear}-${profileData.favoriteRulerKey}`}
                              onEachFeature={() => {}}
                              styleFeature={(feature) => ({
                                fillColor: feature.properties.color || "#12202d",
                                weight: 0,
                                color: "transparent",
                                fillOpacity: feature.properties.highlight ? 0.92 : 0.14,
                              })}
                              paneOpacity={1}
                            />
                          </MapContainer>
                        </div>
                      ) : (
                        <div style={{ color: "#9aa6b8", fontSize: 12 }}>No favorite ruler selected.</div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 11, color: "#c9a84c", textTransform: "uppercase", letterSpacing: 2 }}>Favorite province</div>
                    <div style={{ background: "#08101d", border: "1px solid #1e2e3e", borderRadius: 12, padding: 16 }}>
                      <div style={{ fontSize: 12, color: "#e8dcc8", marginBottom: 8 }}>{selectedProvinceLabel}</div>
                      {profileData.favoriteRegionName ? (
                        <div style={{ height: 220 }}>
                          <MapContainer center={[20, 0]} zoom={2} minZoom={2} style={{ height: "100%", width: "100%" }}>
                            <MapContent
                              mergedGeoJSON={provinceMap}
                              groupedLabels={[]}
                              showLabels={false}
                              showMarkers={false}
                              markerData={null}
                              showBorders={false}
                              colorMode="ruler"
                              geoKey={`view-province-${profileData.favoriteRegionYear}-${profileData.favoriteRegionName}`}
                              onEachFeature={() => {}}
                              styleFeature={(feature) => ({
                                fillColor: feature.properties.color || "#12202d",
                                weight: 0,
                                color: "transparent",
                                fillOpacity: feature.properties.highlight ? 0.92 : 0.14,
                              })}
                              paneOpacity={1}
                            />
                          </MapContainer>
                        </div>
                      ) : (
                        <div style={{ color: "#9aa6b8", fontSize: 12 }}>No favorite province selected.</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ background: "#0d1520", border: "1px solid #1e2e3e", borderRadius: 12, padding: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 12 }}>
                <div>
                  <div style={{ color: "#c9a84c", letterSpacing: 2, fontSize: 10, textTransform: "uppercase" }}>Performance chart</div>
                  <div style={{ fontSize: 12, color: "#9aa6b8", marginTop: 6 }}>Toggle between century performance and score over time.</div>
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {[
                    { label: "Era performance", value: "era" },
                    { label: "Score over time", value: "timeline" },
                  ].map((item) => (
                    <button key={item.value}
                      onClick={() => setOptions((o) => ({ ...o, performanceView: item.value }))}
                      style={{
                        borderRadius: 8,
                        border: `1px solid ${options.performanceView === item.value ? "#c9a84c" : "#1e2e3e"}`,
                        background: options.performanceView === item.value ? "#c9a84c" : "transparent",
                        color: options.performanceView === item.value ? "#0d1117" : "#c9a84c",
                        padding: "8px 14px",
                        cursor: "pointer",
                        fontSize: 11,
                        fontWeight: 700,
                      }}>
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ overflowX: "auto", paddingBottom: 8 }} className="profile-scroll">
                {options.performanceView === "era" ? (
                  <div style={{ minWidth: "3200px", display: "grid", gridTemplateColumns: `repeat(${ERA_KEYS.length}, minmax(80px, 1fr))`, gap: 10 }}>
                    {eraStats.map((era) => {
                      const value = era.scoreAvg;
                      const maxValue = Math.max(...eraStats.map((e) => e.scoreAvg), 1);
                      const pct = Math.round((value / maxValue) * 100);
                      return (
                        <div key={era.key} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                          <div style={{ width: "100%", height: 140, background: "#090f18", borderRadius: 10, display: "flex", alignItems: "flex-end", overflow: "hidden", border: "1px solid #1e2e3e" }}>
                            <div style={{ width: "100%", height: `${Math.max(8, pct)}%`, background: "linear-gradient(180deg, #6fcf97, #1abc9c)", transition: "height 0.3s ease" }} />
                          </div>
                          <div style={{ fontSize: 10, color: "#9aa6b8", textAlign: "center", minHeight: 30 }}>{era.label}</div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#c9a84c" }}>{Math.round(era.scoreAvg)}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ minWidth: "640px", padding: 14 }}>
                    {timelineSeries.length === 0 ? (
                      <div style={{ textAlign: "center", color: "#9aa6b8", padding: 20 }}>No completed games with a date available for the chart.</div>
                    ) : (
                      <div style={{ background: "#090f18", borderRadius: 14, padding: 18, border: "1px solid #1e2e3e" }}>
                        <svg width={Math.max(timelineSeries[0].chartWidth, 640)} height={260} style={{ display: "block", margin: "0 auto" }}>
                          <defs>
                            <linearGradient id="timelineGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#6fcf97" />
                              <stop offset="100%" stopColor="#1abc9c" />
                            </linearGradient>
                          </defs>
                          <rect x={0} y={0} width="100%" height="100%" fill="transparent" />
                          <line x1={40} y1={220} x2={Math.max(timelineSeries[0].chartWidth, 640) - 20} y2={220} stroke="#1e2e3e" strokeWidth={1} />
                          <line x1={40} y1={20} x2={40} y2={220} stroke="#1e2e3e" strokeWidth={1} />
                          {timelineSeries[0].minScore !== timelineSeries[0].maxScore && [0, 0.25, 0.5, 0.75, 1].map((fraction) => {
                            const y = 20 + fraction * 200;
                            const value = Math.round(timelineSeries[0].maxScore - fraction * (timelineSeries[0].maxScore - timelineSeries[0].minScore));
                            return (
                              <g key={fraction}>
                                <line x1={40} y1={y} x2={Math.max(timelineSeries[0].chartWidth, 640) - 20} y2={y} stroke="#11202c" strokeWidth={1} />
                                <text x={34} y={y + 4} textAnchor="end" fontSize="10" fill="#9aa6b8">{value}</text>
                              </g>
                            );
                          })}
                          <polyline
                            fill="none"
                            stroke="url(#timelineGradient)"
                            strokeWidth={3}
                            points={timelineSeries.map((point) => `${point.x},${point.y}`).join(" ")}
                          />
                          {timelineSeries.map((point) => (
                            <g key={point.key}>
                              <circle cx={point.x} cy={point.y} r={5} fill="#6fcf97" stroke="#c9a84c" strokeWidth={2} />
                            </g>
                          ))}
                          {timelineSeries.map((point) => (
                            <g key={`label-${point.key}`}>
                              <text x={point.x} y={238} textAnchor="middle" fontSize="10" fill="#9aa6b8">{point.label}</text>
                            </g>
                          ))}
                        </svg>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div style={{ background: "#0d1520", border: "1px solid #1e2e3e", borderRadius: 12, padding: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 12 }}>
                <div>
                  <div style={{ color: "#c9a84c", letterSpacing: 2, fontSize: 10, textTransform: "uppercase" }}>Game history</div>
                  <div style={{ fontSize: 12, color: "#9aa6b8", marginTop: 6 }}>Paginated history with {options.pageSize} games per page.</div>
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <select value={options.historyOrder} onChange={(e) => setOptions((o) => ({ ...o, historyOrder: e.target.value }))}
                    style={{ padding: 10, borderRadius: 10, border: "1px solid #1e2e3e", background: "#0b1320", color: "#e8dcc8" }}>
                    <option value="recent">Newest first</option>
                    <option value="highest">Best score</option>
                  </select>
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    style={{ ...buttonStyle(page <= 1) }}>
                    Previous
                  </button>
                  <span style={{ fontSize: 11, color: "#e8dcc8" }}>{page} / {pageCount}</span>
                  <button onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    disabled={page >= pageCount}
                    style={{ ...buttonStyle(page >= pageCount) }}>
                    Next
                  </button>
                </div>
              </div>

              {historyLoading && (
                <div style={{ textAlign: "center", color: "#4a5a6a", letterSpacing: 3, fontSize: 11, padding: 20 }}>Loading game history...</div>
              )}

              {!historyLoading && sortedHistory.length === 0 && (
                <div style={{ textAlign: "center", color: "#9aa6b8", padding: 24 }}>No completed games found for this profile yet.</div>
              )}

              {!historyLoading && sortedHistory.map((g) => {
                const date = g.completed_at ? new Date(g.completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Unknown";
                const isOpen = !!expandedGames[g.id];
                return (
                  <div key={g.id} style={{ borderTop: "1px solid #1e2532", padding: "16px 0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", cursor: "pointer" }} onClick={() => setExpandedGames((prev) => ({ ...prev, [g.id]: !prev[g.id] }))}>
                      <div>
                        <div style={{ fontSize: 12, color: "#c9a84c", marginBottom: 4 }}>{date}</div>
                        <div style={{ fontSize: 14, color: "#e8dcc8", fontWeight: 700 }}>Score: {g.total_score}</div>
                      </div>
                      <div style={{ textAlign: "right", fontSize: 12, color: "#9aa6b8" }}>
                        <div>{g.rounds_played} rounds</div>
                        <div>{g.rounds?.length ?? 0} saved rounds</div>
                      </div>
                    </div>
                    {isOpen && (
                      <div style={{ marginTop: 14, padding: 14, background: "#08101d", border: "1px solid #1e2e3e", borderRadius: 10 }}>
                        {g.rounds && g.rounds.length > 0 ? (
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, fontSize: 11, color: "#9aa6b8", marginBottom: 10 }}>
                            <div>Round</div>
                            <div>Actual</div>
                            <div>Guess</div>
                            <div style={{ textAlign: "right" }}>Score</div>
                          </div>
                        ) : null}
                        {g.rounds?.sort((a, b) => a.round_number - b.round_number).map((round) => (
                          <div key={round.round_number} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, alignItems: "center", marginBottom: 8, fontSize: 12, color: "#e8dcc8" }}>
                            <div>#{round.round_number}</div>
                            <div>{round.actual_year}</div>
                            <div>{round.guess_year}</div>
                            <div style={{ textAlign: "right", color: round.score > 600 ? "#6fcf97" : round.score > 250 ? "#f0c040" : "#eb5757" }}>{round.score}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function buttonStyle(disabled) {
  return {
    background: disabled ? "#131b27" : "#c9a84c",
    border: "none",
    color: disabled ? "#4a5a6a" : "#0d1117",
    padding: "8px 12px",
    borderRadius: 8,
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: 11,
    fontWeight: 700,
  };
}
  